import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import {
  getAgreementService,
  updateAgreementService,
} from "../../api/Agreement";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import EditForm from "../../components/EditForm";
import Breadcrumbs from "../../components/Breadcrumbs";
import { getBillingGroupsByAgreement } from "../../api/BillingGroups"; // Import the API function
import AgreementServiceForm from "../../components/agreementService/AgreementServiceForm";

const EditAgreementServicesPage = () => {
  const { getAccessToken } = useAuth();
  const { agreementServicePublicId, agreementPublicId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [agreementService, setAgreementService] = useState(null);
  const [loadingAgreementService, setLoadingAgreementService] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });
  const [billingGroups, setBillingGroups] = useState([]);
  const [fetchTimestamp, setFetchTimestamp] = useState(
    new Date().toISOString()
  );

  useEffect(() => {
    handleGetAgreementService();
    handleGetBillingGroups();
  }, [agreementServicePublicId]);

  const handleGetAgreementService = async () => {
    try {
      const accessToken = await getAccessToken();
      console.log("🔍 agreementServicePublicId:", agreementServicePublicId);

      const newAgreementService = await getAgreementService(
        agreementServicePublicId,
        accessToken
      );

      setAgreementService(newAgreementService);
      setFetchTimestamp(); // Spara tidsstämpeln
    } catch (error) {
      console.error("Failed to fetch AgreementService:", error);
    } finally {
      setLoadingAgreementService(false);
    }
  };

  const handleGetBillingGroups = async () => {
    try {
      const accessToken = await getAccessToken();
      const groups = await getBillingGroupsByAgreement(
        agreementPublicId,
        accessToken
      );
      setBillingGroups(groups);
    } catch (error) {
      console.error("Failed to fetch billing groups:", error);
    }
  };

  const handleBackClick = () => {
    if (hasChanges) {
      setConfirmState((s) => ({
        ...s,
        isOpen: true,
        title: "Discard changes?",
        confirm: () => {
          navigate(-1);
        },
      }));
    } else {
      navigate(-1);
    }
  };

  const handleBillingGroupChange = (selectedName) => {
    const selectedGroup = billingGroups.find(
      (group) => group.name === selectedName
    );
    if (!selectedGroup) return;

    setAgreementService((prevAgreementService) => ({
      ...prevAgreementService,
      billingGroupName: selectedGroup.name,
      billingGroupPublicId: selectedGroup.billingGroupPublicId,
      billingGroupStatus: selectedGroup.status || "Active", // Ensure status is updated
    }));

    setHasChanges(true);
  };

  const handleUpdateAgreementService = async () => {
    try {
      const accessToken = await getAccessToken();

      if (!accessToken) {
        toast.error("Authorization failed. Please log in again.");
        return;
      }

      await updateAgreementService(agreementService, accessToken);

      toast.success("AgreementService updated successfully!", {
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });

      setHasChanges(false);
    } catch (error) {
      toast.error(error.message || "Failed to save AgreementService");
    }
  };

  const saveAndExit = () => {
    handleUpdateAgreementService();
  };

  console.log("SERVICE:", agreementService);

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute w-full pointer-events-none">
            <LoadingTablePage
              title={"Agreement service"}
              loading={loadingAgreementService}
            />
          </div>
          <div
            className={`${
              loadingAgreementService ? "loading" : ""
            } page-content`}
          >
            <Breadcrumbs />

            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">
                {`Edit agreement service: ${
                  agreementService?.name || "Unknown"
                }`}
              </h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!hasChanges}
                  onClick={saveAndExit}
                >
                  Save
                </button>
              </div>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              <AgreementServiceForm
                agreementService={agreementService}
                setAgreementService={setAgreementService}
                setHasChanges={setHasChanges}
                disabledFields={[
                  "isActiveForAgreement",
                  "agreementServicePublicId",
                  "comment",
                ]}
              />

              {/*               <EditForm
                data={product || {}}
                setData={setProduct}
                setHasChanges={setHasChanges}
                disabled={false}
                fields={formFields}
                dropdownOptions={{
                  billingGroupName: billingGroups.map((g) => ({
                    name: g.name,
                    billingGroupPublicId: g.billingGroupPublicId,
                  })),
                }}
              /> */}
            </div>
          </div>
        </main>
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={confirmState.title ? confirmState.title : "Discard changes?"}
          onClose={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
          }}
          onConfirm={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
            confirmState.confirm();
          }}
        />
      </div>
    </>
  );
};

export default EditAgreementServicesPage;
